import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { API_BASE_URL, createAPIClient } from './api.config'
import { queryKeys } from './api.queryKeys'
import { getStudentRegistrationScope } from '../../utils/placement-tracker/placementCycles'

const MAX_CYCLES = 100
const DEFAULT_PAGE_SIZE = 100

const normalizeJob = job => {
    if ( !job || typeof job !== 'object' || Array.isArray( job ) ) return job
    if ( job.job_location === undefined ) return job
    return { ...job, location: job.location ?? job.job_location }
}

const normalizeJobs = jobs => Array.isArray( jobs ) ? jobs.map( normalizeJob ) : normalizeJob( jobs )

const invalidateJobQueries = qc => Promise.all( [
    queryKeys.placementTracker.jobs.all,
    queryKeys.placementTracker.dashboard.all,
    queryKeys.placementTracker.student.all,
].map( queryKey => qc.invalidateQueries( { queryKey } ) ) )

const invalidatePlacementCycleQueries = qc => Promise.all( [
    queryKeys.placementTracker.cycles.all,
    queryKeys.placementTracker.jobs.all,
    queryKeys.placementTracker.dashboard.all,
    queryKeys.placementTracker.jobApplications.all,
    queryKeys.placementTracker.students.all,
    queryKeys.placementTracker.student.all,
].map( queryKey => qc.invalidateQueries( { queryKey } ) ) )

class PlacementTrackerAPI {

    apiClient = createAPIClient( { baseURL: API_BASE_URL } )

    coordinator = {}

    student = {}

    cycles = {}

    skills = {}

    constructor () {
        this._initializeCoordinator()
        this._initializeCoordinatorCompanies()
        this._initializeOutreach()
        this._initializeJobs()
        this._initializeJobApplications()
        this._initializeOffers()
        this._initializeStudent()
        this._initializeCycles()
        this._initializeDashboard()
        this._initializeSkillsCatalog()
    }

    // ==================== COORDINATOR / COMPANIES ====================
    _initializeCoordinatorCompanies () {
        this.coordinator.companies = {
            useCompanies: ( opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.companies.list(),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( '/coordinator/companies' )
                        return data.data || data
                    },
                    ...opts,
                } ),

            useCompanyDetail: ( id, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.companies.detail( id ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( `/practice/companies/${encodeURIComponent( id )}` )
                        return data.data ?? null
                    },
                    enabled: !!id,
                    ...opts,
                } ),

            useCreate: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( payload ) => this.apiClient.post( '/coordinator/companies', payload ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.companies.list() } ),
                    ...opts,
                } )
            },

            useUpdate: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { companyId, payload } ) => this.apiClient.put( `/practice/companies/${encodeURIComponent( companyId )}`, payload ),
                    onSuccess: () => {
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.companies.all } )
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.jobs.all } )
                    },
                    ...opts,
                } )
            },

            useDelete: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { companyId } ) => this.apiClient.delete( `/practice/companies/${encodeURIComponent( companyId )}` ),
                    onSuccess: ( _, { companyId } ) => {
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.companies.list() } )
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.companies.detail( companyId ), refetchType: 'none' } )
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.jobs.all } )
                    },
                    ...opts,
                } )
            },
        }
    }

    // ==================== COORDINATOR / OUTREACH ====================
    _initializeOutreach () {
        this.coordinator.outreach = {
            useList: ( filters = {}, opts = {} ) => {
                const limit = filters.limit || 100
                return useInfiniteQuery( {
                    queryKey: queryKeys.placementTracker.outreach.list( filters ),
                    queryFn: async ( { pageParam } ) => {
                        const { data } = await this.apiClient.get( '/tracker/outreach', { params: { ...filters, limit, offset: pageParam } } )
                        return data.data || data
                    },
                    initialPageParam: 0,
                    getNextPageParam: ( lastPage ) => ( lastPage?.length === limit ? lastPage.length : undefined ),
                    ...opts,
                } )
            },

            useCreate: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( payload ) => this.apiClient.post( '/tracker/outreach', payload ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.outreach.all } ),
                    ...opts,
                } )
            },

            useUpdate: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { outreachId, payload } ) => this.apiClient.patch( `/tracker/outreach/${outreachId}`, payload ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.outreach.all } ),
                    ...opts,
                } )
            },

            useDelete: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { outreachId } ) => this.apiClient.delete( `/tracker/outreach/${outreachId}` ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.outreach.all } ),
                    ...opts,
                } )
            },
        }
    }

    // ==================== PLACEMENT CYCLES ====================
    _initializeCycles () {
        this.cycles = {
            useAll: ( opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.cycles.list(),
                    queryFn: async ( { signal } ) => {
                        const cycles = []
                        let page
                        do {
                            const { data } = await this.apiClient.get( '/tracker/placement-cycles', { params: { limit: MAX_CYCLES, offset: cycles.length }, signal } )
                            page = data.data || data
                            cycles.push( ...page )
                        } while ( page.length === MAX_CYCLES )
                        return cycles
                    },
                    ...opts,
                } ),

            useCreate: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    ...opts,
                    mutationFn: payload => this.apiClient.post( '/tracker/placement-cycles', payload ),
                    onSuccess: async ( ...args ) => {
                        await invalidatePlacementCycleQueries( qc )
                        await opts.onSuccess?.( ...args )
                    },
                } )
            },

            useUpdate: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    ...opts,
                    mutationFn: ( { cycleId, payload } ) => this.apiClient.patch( `/tracker/placement-cycles/${encodeURIComponent( cycleId )}`, payload ),
                    onSuccess: async ( ...args ) => {
                        await invalidatePlacementCycleQueries( qc )
                        await opts.onSuccess?.( ...args )
                    },
                } )
            },

            useDelete: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    ...opts,
                    mutationFn: ( { cycleId } ) => this.apiClient.delete( `/tracker/placement-cycles/${encodeURIComponent( cycleId )}` ),
                    onSuccess: async ( ...args ) => {
                        await invalidatePlacementCycleQueries( qc )
                        await opts.onSuccess?.( ...args )
                    },
                } )
            },
        }
    }

    // ==================== SKILL CATALOG (any logged-in user) ====================
    _initializeSkillsCatalog () {
        this.skills = {
            useCatalog: ( filters = {}, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.skills.list( filters ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( '/tracker/skills', { params: filters } )
                        return data.data || data
                    },
                    ...opts,
                } ),

            useCreate: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( payload ) => this.apiClient.post( '/tracker/skills', payload ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.skills.all } ),
                    ...opts,
                } )
            },
        }
    }

    // ==================== COORDINATOR / DASHBOARD STATS ====================
    _initializeDashboard () {
        this.coordinator.dashboard = {
            useStats: ( filters = {}, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.dashboard.stats( filters ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( '/coordinator/reports/dashboard', { params: filters } )
                        return data.data || data
                    },
                    ...opts,
                } ),

            useDashboardV2: ( filters = {}, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.dashboard.dashboardV2( filters ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( '/coordinator/reports/dashboard-v2', { params: filters } )
                        return data.data || data
                    },
                    ...opts,
                } ),
        }
    }

    // ==================== COORDINATOR / STUDENTS ====================
    _initializeCoordinator () {
        Object.assign( this.coordinator, {
            useStudents: ( filters = {}, opts = {} ) => {
                const limit = filters.limit || DEFAULT_PAGE_SIZE
                return useInfiniteQuery( {
                    queryKey: queryKeys.placementTracker.students.list( filters ),
                    queryFn: async ( { pageParam } ) => {
                        const { data } = await this.apiClient.get( '/coordinator/students', { params: { ...filters, limit, offset: pageParam } } )
                        return data
                    },
                    initialPageParam: 0,
                    getNextPageParam: ( lastPage ) => lastPage.pagination?.next_offset ?? undefined,
                    ...opts,
                } )
            },

            useStudentDetail: ( id, filters = {}, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.students.detail( id, filters ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( `/tracker/students/${id}`, { params: filters } )
                        return data.data || data
                    },
                    enabled: !!id,
                    ...opts,
                } ),

            useStudentDetailByAssignment: ( studentAssignmentId, filters = {}, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.students.detail( `assignment:${studentAssignmentId}`, filters ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( `/tracker/students/assignments/${studentAssignmentId}`, { params: filters } )
                        return data.data || data
                    },
                    enabled: !!studentAssignmentId,
                    ...opts,
                } ),

            useUpdateStudent: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { id, payload } ) => this.apiClient.patch( `/coordinator/students/${id}`, payload ),
                    onSuccess: ( _, { id, filters = {} } ) => {
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.students.list( filters ) } )
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.students.detail( id ) } )
                    },
                    ...opts,
                } )
            },

            useDeleteStudent: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { id, force_delete } ) => this.apiClient.delete( `/tracker/students/${id}`, { data: { force_delete } } ),
                    onSuccess: ( _, { refreshFilters = {} } ) => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.students.list( refreshFilters ) } ),
                    ...opts,
                } )
            },

            useImportStudents: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( payload ) => this.apiClient.post( '/coordinator/students/import', payload ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.students.list( {} ) } ),
                    ...opts,
                } )
            },

            // studentAssignmentId is the registration's uuid (not the AUID). Returns every submission
            // of that one registration (pending and reviewed) from the student-profile review queue.
            useStudentSubmissions: ( studentAssignmentId, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.students.submissions( studentAssignmentId ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( `/coordinator/student-profile/${studentAssignmentId}`, { params: { limit: 100 } } )
                        const submissions = data.data || data
                        return [...submissions].sort( ( a, b ) => new Date( b.submitted_at ) - new Date( a.submitted_at ) )
                    },
                    enabled: !!studentAssignmentId,
                    ...opts,
                } ),

            useReviewStudentSubmission: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { submissionId, payload } ) => this.apiClient.patch( `/coordinator/student-profiles/${submissionId}/review`, payload ),
                    onSuccess: ( _, { studentListFilters = {}, studentId, studentAssignmentId } ) => {
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.students.list( studentListFilters ) } )
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.students.submissions( studentAssignmentId ) } )
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.students.detail( studentId ) } )
                    },
                    ...opts,
                } )
            },
        } )
    }

    // ==================== COORDINATOR / JOBS ====================
    _initializeJobs () {
        this.coordinator.jobs = {
            useJobs: ( filters = {}, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.jobs.list( filters ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( '/coordinator/jobs', { params: filters } )
                        return normalizeJobs( data.data || data )
                    },
                    ...opts,
                } ),

            useJobDetail: ( jobId, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.jobs.detail( jobId ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( `/tracker/jobs/${jobId}` )
                        return normalizeJob( data.data || data )
                    },
                    enabled: !!jobId,
                    ...opts,
                } ),

            useCreateJob: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( payload ) => this.apiClient.post( '/coordinator/jobs', payload ),
                    onSuccess: () => invalidateJobQueries( qc ),
                    ...opts,
                } )
            },

            useUpdateJob: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { jobId, payload } ) => this.apiClient.patch( `/coordinator/jobs/${jobId}`, payload ),
                    onSuccess: ( response, { jobId } ) => {
                        const updatedJob = normalizeJob( response?.data?.data )
                        if ( updatedJob ) {
                            qc.setQueryData( queryKeys.placementTracker.jobs.detail( jobId ), currentJob => ( { ...currentJob, ...updatedJob } ) )
                        }
                        return invalidateJobQueries( qc )
                    },
                    ...opts,
                } )
            },

            useDeleteJob: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { jobId } ) => this.apiClient.delete( `/coordinator/jobs/${jobId}` ),
                    onSuccess: () => invalidateJobQueries( qc ),
                    ...opts,
                } )
            },
        }
    }

    // ==================== COORDINATOR / JOB APPLICATIONS ====================
    _initializeJobApplications () {
        this.coordinator.jobApplications = {
            useApplications: ( filters = {}, opts = {} ) => {
                const limit = filters.limit || DEFAULT_PAGE_SIZE
                return useInfiniteQuery( {
                    queryKey: queryKeys.placementTracker.jobApplications.list( filters ),
                    queryFn: async ( { pageParam } ) => {
                        const { data } = await this.apiClient.get( '/coordinator/job-applications', { params: { ...filters, limit, offset: pageParam } } )
                        return data
                    },
                    initialPageParam: 0,
                    getNextPageParam: ( lastPage ) => lastPage.pagination?.next_offset ?? undefined,
                    ...opts,
                } )
            },

            useApplicationsByJob: ( jobId, opts = {} ) =>
                useInfiniteQuery( {
                    queryKey: queryKeys.placementTracker.jobApplications.list( { job_id: jobId } ),
                    queryFn: async ( { pageParam } ) => {
                        const { data } = await this.apiClient.get( '/coordinator/job-applications', { params: { job_id: jobId, limit: 50, offset: pageParam } } )
                        return data
                    },
                    initialPageParam: 0,
                    getNextPageParam: ( lastPage ) => lastPage.pagination?.next_offset ?? undefined,
                    enabled: !!jobId,
                    ...opts,
                } ),

            useUpdateJobApplication: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { applicationId, payload } ) => this.apiClient.patch( `/coordinator/job-applications/${applicationId}`, payload ),
                    onSuccess: ( _, { jobId } ) => {
                        if ( jobId ) qc.invalidateQueries( { queryKey: queryKeys.placementTracker.jobApplications.list( { job_id: jobId } ) } )
                    },
                    ...opts,
                } )
            },
        }
    }

    // ==================== COORDINATOR / OFFERS ====================
    _initializeOffers () {
        this.coordinator.offers = {
            getOfferLetterUrl: async ( offerId ) => {
                const { data } = await this.apiClient.get( `/coordinator/offers/${offerId}/letter` )
                return data.data.url
            },

            // Coordinator upload/replace: PATCH /offers/:offer_id as multipart with an `offer_letter` file.
            useUploadOfferLetter: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { offerId, file } ) => {
                        const formData = new FormData()
                        formData.append( 'offer_letter', file )
                        return this.apiClient.patch( `/coordinator/offers/${offerId}`, formData )
                    },
                    onSuccess: () => {
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.jobApplications.all } )
                    },
                    ...opts,
                } )
            },
        }
    }

    // ==================== STUDENT SELF-SERVICE ====================
    _initializeStudent () {
        this.student = {
            useDashboard: ( filters = {}, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.student.dashboard( filters ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( '/user/placements/dashboard', { params: filters } )
                        return data.data || data
                    },
                    ...opts,
                } ),

            useCompanyDetail: ( id, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.companies.detail( id ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( `/practice/companies/${encodeURIComponent( id )}` )
                        return data.data ?? null
                    },
                    enabled: !!id,
                    ...opts,
                } ),

            useEligibleJobs: ( filters = {}, opts = {} ) =>
                useInfiniteQuery( {
                    queryKey: queryKeys.placementTracker.student.eligibleJobs( filters ),
                    queryFn: async ( { pageParam } ) => {
                        const { data } = await this.apiClient.get( '/user/placements/eligible-jobs', { params: { ...filters, limit: filters.limit || 20, offset: pageParam } } )
                        return data?.data ? { ...data, data: normalizeJobs( data.data ) } : data
                    },
                    initialPageParam: 0,
                    getNextPageParam: ( lastPage ) => lastPage.pagination?.next_offset ?? undefined,
                    ...opts,
                } ),

            useJobDetail: ( jobId, filters = {}, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.jobs.detail( jobId, filters ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( `/tracker/jobs/${jobId}`, { params: filters } )
                        return normalizeJob( data.data || data )
                    },
                    enabled: !!jobId,
                    ...opts,
                } ),

            useApplications: ( filters = {}, opts = {} ) =>
                useInfiniteQuery( {
                    queryKey: queryKeys.placementTracker.student.applications( filters ),
                    queryFn: async ( { pageParam } ) => {
                        const { data } = await this.apiClient.get( '/user/placements/applications', { params: { ...filters, limit: filters.limit || 20, offset: pageParam } } )
                        return data
                    },
                    initialPageParam: 0,
                    getNextPageParam: ( lastPage ) => lastPage.pagination?.next_offset ?? undefined,
                    ...opts,
                } ),

            useUploadOfferLetter: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { jobId, file, ...scope } ) => {
                        const formData = new FormData()
                        formData.append( 'offer_letter', file )
                        Object.entries( getStudentRegistrationScope( scope ) ).forEach( ( [key, value] ) => formData.append( key, value ) )
                        return this.apiClient.post( `/user/placements/jobs/${jobId}/offer-letter`, formData )
                    },
                    onSuccess: () => {
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.student.all } )
                    },
                    ...opts,
                } )
            },

            useApplyToJob: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { jobId, ...scope } ) => this.apiClient.post( '/user/placements/job-applications', { job_id: jobId, ...getStudentRegistrationScope( scope ) } ),
                    onSuccess: () => {
                        qc.invalidateQueries( { queryKey: queryKeys.placementTracker.student.all } )
                    },
                    ...opts,
                } )
            },

            useJobTimeline: ( jobId, filters = {}, opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.student.jobTimeline( jobId, filters ),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( `/user/placements/jobs/${jobId}/timeline`, { params: filters } )
                        return data.data || data
                    },
                    enabled: !!jobId,
                    ...opts,
                } ),

            useResumes: ( opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.student.resumes(),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( '/user/placements/resumes' )
                        return data.data || data
                    },
                    ...opts,
                } ),

            useUploadResume: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { file, title } ) => {
                        const formData = new FormData()
                        formData.append( 'resume', file )
                        formData.append( 'title', title )
                        return this.apiClient.post( '/user/placements/resumes', formData )
                    },
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.student.resumes() } ),
                    ...opts,
                } )
            },

            useDeleteResume: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { resumeId } ) => this.apiClient.delete( `/user/placements/resumes/${resumeId}` ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.student.resumes() } ),
                    ...opts,
                } )
            },

            useSkills: ( opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.student.skills(),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( '/user/placements/skills' )
                        return data.data || data
                    },
                    ...opts,
                } ),

            useLinkSkills: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { skills } ) => this.apiClient.post( '/user/placements/skills', { skills } ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.student.skills() } ),
                    ...opts,
                } )
            },

            useUnlinkSkill: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { name } ) => this.apiClient.delete( `/user/placements/skills/${encodeURIComponent( name )}` ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.student.skills() } ),
                    ...opts,
                } )
            },

            // Rows: { certificate_id, skill_assignment_id, skill_name, skill_category, certificate_name, certificate_key, certificate_file_details, uploaded_at, certificate_view_url }
            // `certificate_view_url` is signed and expires, so refetch rather than storing it.
            useSkillCertificates: ( opts = {} ) =>
                useQuery( {
                    queryKey: queryKeys.placementTracker.student.skillCertificates(),
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( '/user/placements/skills/certificates' )
                        return data.data || data
                    },
                    ...opts,
                } ),

            useUploadSkillCertificate: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { skillAssignmentId, file, certificateName } ) => {
                        const formData = new FormData()
                        formData.append( 'certificate', file )
                        formData.append( 'certificate_name', certificateName )
                        return this.apiClient.post( `/user/placements/skills/${skillAssignmentId}/certificates`, formData )
                    },
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.student.skillCertificates() } ),
                    ...opts,
                } )
            },

            useDeleteSkillCertificate: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { skillAssignmentId, certificateId } ) => this.apiClient.delete( `/user/placements/skills/${skillAssignmentId}/certificates/${certificateId}` ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.student.skillCertificates() } ),
                    ...opts,
                } )
            },

            useWithdrawApplication: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { jobId, reason } ) => this.apiClient.post( `/user/placements/jobs/${jobId}/withdraw`, { reason } ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.student.all } ),
                    ...opts,
                } )
            },

            // Hard-deletes the student's own application (no reason needed) via the withdraw endpoint with `delete: true`.
            useDeleteApplication: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { jobId } ) => this.apiClient.post( `/user/placements/jobs/${jobId}/withdraw`, { delete: true } ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.student.all } ),
                    ...opts,
                } )
            },

            // Drives the profile form and the "awaiting approval" gate on the jobs portal.
            // data: { identity, cycle, registration, verification_status, verification_note,
            //   has_pending_submission, pending_submission, can_apply, can_submit, can_update, can_resubmit }
            // `params` (e.g. { placement_cycle_id }) scopes the profile to a specific cycle.
            useProfile: ( { params, ...opts } = {} ) =>
                useQuery( {
                    queryKey: [...queryKeys.placementTracker.student.profile(), params ?? {}],
                    queryFn: async () => {
                        const { data } = await this.apiClient.get( '/user/placements/profile', { params } )
                        return data.data || data
                    },
                    ...opts,
                } ),

            // POST the first time (no registration yet in the open cycle), PATCH afterwards — to update
            // an approved registration (kept active until the change is reviewed) or to resubmit a
            // rejected one (applied immediately, back to pending review). Callers pass
            // `isInitialSubmission` from useProfile()'s `can_submit` flag to pick the right endpoint.
            useSubmitProfile: ( opts = {} ) => {
                const qc = useQueryClient()
                return useMutation( {
                    mutationFn: ( { isInitialSubmission, ...payload } ) =>
                        isInitialSubmission
                            ? this.apiClient.post( '/user/placements/profile', payload )
                            : this.apiClient.patch( '/user/placements/profile', payload ),
                    onSuccess: () => qc.invalidateQueries( { queryKey: queryKeys.placementTracker.student.profile() } ),
                    ...opts,
                } )
            },
        }
    }
}

// Create and export a singleton instance
const placementTrackerAPI = new PlacementTrackerAPI()

export default placementTrackerAPI
